import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';

const themes = [
  {
    title: '寫真文化',
    desc: '以影像記錄地方，打造獨特的小鎮品牌。推動「寫真之町」政策，凝聚居民認同。',
    icon: 'Camera',
    image: 'https://images.unsplash.com/photo-1516961642265-531546e84af2?w=500&h=400&fit=crop&q=80'
  },
  {
    title: '職人家具',
    desc: '傳承工藝精神，展現地方產業的生命力。結合在地木材，打造溫潤的高品質生活道具。',
    icon: 'PenTool',
    image: 'https://images.unsplash.com/photo-1611089201502-0e318f77af4f?w=500&h=400&fit=crop&q=80'
  },
  {
    title: '大雪山文化',
    desc: '依傍自然的生態智慧，實踐永續生活的可能。善用純淨的地下水資源，孕育獨特風土。',
    icon: 'Mountain',
    image: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=500&h=400&fit=crop&q=80'
  }
];

const goals = [
  { title: '深度探索', desc: '親臨現場探索小鎮再生的秘密，與世界對話。', icon: 'Search' },
  { title: '社群共學', desc: '與志同道合的見學夥伴展開社群共學，建立長期的連結。', icon: 'Users' },
  { title: '地域重生', desc: '實際走進地域重生的旅程，激發對社會創新的想像。', icon: 'Sun' }
];

const HigashikawaInfo = () => {
  return (
    <div className="py-20">
      <div className="container mx-auto px-4 md:px-6">
        
        {/* Background Story */}
        <div className="max-w-4xl mx-auto mb-24">
          <div className="text-center mb-12">
            <span className="text-teal-600 font-bold tracking-wider uppercase text-sm mb-2 block">Story of Rebirth</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">東川町的重生奇蹟</h2>
          </div>
          
          <div className="bg-white p-8 md:p-12 rounded-3xl shadow-[0_20px_50px_rgb(0,0,0,0.05)] border border-gray-100 flex flex-col md:flex-row gap-10 items-center relative overflow-hidden">
            <div className="absolute top-0 left-0 w-2 h-full bg-teal-500"></div>
            <div className="flex-1 space-y-6 text-lg text-gray-700 leading-relaxed">
              <p>
                東川町曾面臨嚴重的人口老化與流失危機，1990 年代人口一度跌破 7,000 人。然而，透過強烈的危機感，當地政府與居民攜手推動<strong className="text-teal-700">「寫真之町」</strong>政策，並結合日語教育吸引外地人移居。
              </p>
              <p>
                過去 20 年來，東川町成為北海道唯二人口正成長的地方之一。2024 年更獲日本房屋租賃與建設龍頭企業「大東建託株式會社」選為<strong className="text-teal-700 bg-teal-50 px-2 py-1 rounded">「全日本最宜居的地方」</strong>。
              </p>
            </div>
            <div className="w-full md:w-1/3 flex flex-col gap-4 text-center">
              <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
                <div className="text-4xl font-black text-teal-600 mb-2">Top 1</div>
                <div className="text-sm font-bold text-gray-500">2024 全日本最宜居</div>
              </div>
              <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
                <div className="text-4xl font-black text-teal-600 mb-2">+ 20<span className="text-xl">年</span></div>
                <div className="text-sm font-bold text-gray-500">持續人口正成長</div>
              </div>
            </div>
          </div>
        </div>

        {/* Core Themes */}
        <div className="mb-24">
          <div className="text-center mb-12">
            <span className="text-teal-600 font-bold tracking-wider uppercase text-sm mb-2 block">Core Themes</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">見學核心價值</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">東川町將以下三大元素深度融入日常生活，打造創新且具活力的發展道路：</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {themes.map((theme, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-300 group"
              >
                <div className="h-48 overflow-hidden">
                  <img src={theme.image} alt={theme.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-3 mb-4 text-teal-600">
                    <SafeIcon name={theme.icon} className="w-6 h-6" />
                    <h3 className="text-2xl font-bold text-gray-900">{theme.title}</h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed">{theme.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Goals */}
        <div className="bg-teal-900 rounded-3xl p-8 md:p-16 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 opacity-10 transform translate-x-1/4 -translate-y-1/4">
            <SafeIcon name="Target" className="w-96 h-96" />
          </div>
          <div className="relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">見學計畫目標</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
              {goals.map((goal, idx) => (
                <div key={idx} className="flex flex-col items-center text-center space-y-4">
                  <div className="w-16 h-16 rounded-full bg-teal-800 flex items-center justify-center text-teal-300 shadow-inner">
                    <SafeIcon name={goal.icon} className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-bold text-white">{goal.title}</h3>
                  <p className="text-teal-100">{goal.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default HigashikawaInfo;